import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Truck, 
  SignOut, 
  Phone, 
  MapPin, 
  NavigationArrow,
  Play,
  Flag,
  CheckCircle,
  CurrencyEur,
  Warning,
  Clock,
  CalendarBlank
} from '@phosphor-icons/react';
import StatusBadge from '../components/app/StatusBadge';
import CountdownTimer from '../components/app/CountdownTimer';

const API = process.env.REACT_APP_BACKEND_URL;

// Format date as "Lun. 14 avril — 14h30"
const formatFullDateTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  const days = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
  const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  
  const dayName = days[date.getDay()];
  const dayNum = date.getDate();
  const month = months[date.getMonth()];
  const hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${dayName} ${dayNum} ${month} — ${hours}h${minutes}`;
};

// Check if a date is today (Paris timezone)
const isToday = (isoString) => {
  if (!isoString) return false;
  const date = new Date(isoString);
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Check if a date is in the future (tomorrow or later)
const isFuture = (isoString) => {
  if (!isoString) return false;
  const date = new Date(isoString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return date >= tomorrow;
};

// Reverse geocode using Nominatim with postal code
const reverseGeocode = async (lat, lng) => {
  try {
    const response = await axios.get(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      { headers: { 'Accept-Language': 'fr' } }
    );
    const data = response.data;
    const address = data.address || {};
    
    // Build address with postal code
    const street = address.road || address.pedestrian || address.footway || '';
    const houseNumber = address.house_number || '';
    const postcode = address.postcode || '';
    const city = address.city || address.town || address.village || address.municipality || '';
    
    let formattedAddress = '';
    if (houseNumber && street) {
      formattedAddress = `${houseNumber} ${street}`;
    } else if (street) {
      formattedAddress = street;
    }
    
    if (postcode && city) {
      formattedAddress += formattedAddress ? `, ${postcode} ${city}` : `${postcode} ${city}`;
    } else if (city) {
      formattedAddress += formattedAddress ? `, ${city}` : city;
    }
    
    return formattedAddress || data.display_name || 'Adresse inconnue';
  } catch (e) {
    console.error('Reverse geocode error:', e);
    return null;
  }
};

export default function TechnicianDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInterventions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/interventions`, {
        headers: getAuthHeaders()
      });
      // Filter for technician: assigned to them or validated/pending
      const filtered = response.data.filter(i => 
        i.technician_id === user?.id ||
        (i.status === 'validee' && !i.technician_id)
      );
      setInterventions(filtered);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchInterventions();
    const interval = setInterval(fetchInterventions, 10000);
    return () => clearInterval(interval);
  }, [fetchInterventions]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const selected = interventions.find(i => i.id === selectedId);

  // Split interventions into categories
  const todayInterventions = interventions.filter(i => {
    const announcedTime = i.announced_time || i.created_at;
    return isToday(announcedTime);
  });

  const futureInterventions = interventions.filter(i => {
    const announcedTime = i.announced_time || i.created_at;
    return isFuture(announcedTime);
  });

  // Sort today's interventions: active first, then completed
  const sortedTodayInterventions = [...todayInterventions].sort((a, b) => {
    const completedStatuses = ['terminee', 'encaissee'];
    const aCompleted = completedStatuses.includes(a.status);
    const bCompleted = completedStatuses.includes(b.status);
    
    if (aCompleted !== bCompleted) {
      return aCompleted ? 1 : -1; // Active first
    }
    
    // Within same category, sort by announced time
    return new Date(a.announced_time) - new Date(b.announced_time);
  });

  // Sort future interventions chronologically
  const sortedFutureInterventions = [...futureInterventions].sort((a, b) => {
    return new Date(a.announced_time) - new Date(b.announced_time);
  });

  const updateStatus = async (status, extraData = {}) => {
    if (!selectedId) return;
    setActionLoading(true);
    
    try {
      // Get current position for all steps
      let geoData = {};
      try {
        const pos = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { 
            timeout: 10000,
            enableHighAccuracy: true 
          });
        });
        geoData.technician_lat = pos.coords.latitude;
        geoData.technician_lng = pos.coords.longitude;
        
        // Reverse geocode to get address with postal code
        const address = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
        if (address) {
          geoData.technician_address = address;
        }
      } catch (e) {
        console.log('Geolocation not available:', e.message);
        // Continue without position
      }

      await axios.post(
        `${API}/api/interventions/${selectedId}/status`,
        { status, ...geoData, ...extraData },
        { headers: getAuthHeaders() }
      );

      // SMS is now sent automatically by the backend status trigger
      // No manual SMS call needed on departure

      toast.success('Statut mis à jour');
      fetchInterventions();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const openWaze = (address) => {
    const encoded = encodeURIComponent(address);
    window.open(`https://waze.com/ul?q=${encoded}`, '_blank');
  };

  const isLate = (intervention) => {
    if (!intervention.announced_time || intervention.status !== 'validee') return false;
    const announced = new Date(intervention.announced_time);
    return new Date() > announced;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-md mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Truck size={24} weight="fill" className="text-reco-500" />
              <span className="font-heading font-bold text-gray-900">
                <span className="text-reco-500">reco</span>pneus
              </span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              className="text-gray-500"
              data-testid="tech-logout"
            >
              <SignOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6">
        {!selectedId ? (
          // Intervention List with Two Categories
          <div className="space-y-6">
            {/* Today's Interventions */}
            <section>
              <h2 className="text-lg font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CalendarBlank size={20} className="text-reco-500" />
                Interventions du jour
              </h2>
              
              {loading ? (
                <LoadingCards />
              ) : sortedTodayInterventions.length === 0 ? (
                <EmptyState message="Aucune intervention pour aujourd'hui" />
              ) : (
                <div className="space-y-3">
                  {sortedTodayInterventions.map(intervention => (
                    <InterventionListCard
                      key={intervention.id}
                      intervention={intervention}
                      onClick={() => setSelectedId(intervention.id)}
                      isLate={isLate(intervention)}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Future Interventions */}
            {sortedFutureInterventions.length > 0 && (
              <section>
                <h2 className="text-lg font-heading font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock size={20} className="text-blue-500" />
                  Interventions à venir
                </h2>
                
                <div className="space-y-3">
                  {sortedFutureInterventions.map(intervention => (
                    <InterventionListCard
                      key={intervention.id}
                      intervention={intervention}
                      onClick={() => setSelectedId(intervention.id)}
                      isLate={false}
                      isFuture={true}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        ) : (
          // Intervention Detail
          <div className="space-y-6">
            <Button
              variant="ghost"
              onClick={() => setSelectedId(null)}
              className="text-gray-500 -ml-2"
            >
              ← Retour
            </Button>

            {selected && (
              <>
                {isLate(selected) && selected.status === 'validee' && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 animate-pulse">
                    <Warning size={20} weight="fill" />
                    <span className="font-semibold">Attention — Vous êtes en retard sur cette intervention</span>
                  </div>
                )}

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-heading font-bold text-gray-900">
                        {selected.client_name}
                      </h2>
                      <StatusBadge status={selected.status} />
                    </div>
                    <CountdownTimer
                      announcedTime={selected.announced_time}
                      validatedAt={selected.validated_at}
                      status={selected.status}
                    />
                  </div>

                  {/* Date and Time */}
                  <div className="flex items-center gap-2 text-gray-600 mb-4 bg-gray-50 rounded-lg p-3">
                    <CalendarBlank size={18} className="text-reco-500" />
                    <span className="font-medium">{formatFullDateTime(selected.announced_time)}</span>
                  </div>

                  {/* Phone */}
                  <a
                    href={`tel:${selected.client_phone}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl mb-4 hover:bg-gray-100 transition-colors"
                    data-testid="tech-call-client"
                  >
                    <Phone size={24} className="text-reco-500" />
                    <span className="text-gray-900 text-lg">{selected.client_phone}</span>
                  </a>

                  {/* Info */}
                  <div className="space-y-3 mb-6">
                    <InfoItem label="Type" value={selected.intervention_type} />
                    <InfoItem label="Adresse" value={selected.address} />
                    {selected.tire_dimension && (
                      <InfoItem label="Pneus" value={`${selected.tire_dimension} ${selected.tire_type || ''} ${selected.tire_brand || ''}`} />
                    )}
                    {selected.selected_stock_quantity && (
                      <InfoItem label="Quantité pneus" value={`${selected.selected_stock_quantity}`} />
                    )}
                    {(selected.vehicle_plate || selected.vehicle_brand) && (
                      <InfoItem label="Véhicule" value={`${selected.vehicle_brand || ''} ${selected.vehicle_model || ''} ${selected.vehicle_plate ? `— ${selected.vehicle_plate}` : ''}`} />
                    )}
                    {selected.tire_source && (
                      <InfoItem label="Source pneus" value={selected.tire_source} />
                    )}
                  </div>

                  {/* Notes */}
                  {selected.notes && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                      <div className="text-xs font-semibold text-amber-600 uppercase mb-1">Notes</div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{selected.notes}</p>
                    </div>
                  )}

                  {/* Price */}
                  <div className="bg-reco-50 border border-reco-200 rounded-xl p-4 mb-6">
                    <div className="text-sm text-reco-600 mb-1">Prix à encaisser</div>
                    <div className="flex items-center gap-2 text-3xl font-bold text-reco-600">
                      <CurrencyEur size={28} />
                      <span>{selected.price?.toFixed(2)} €</span>
                    </div>
                  </div>

                  {/* Waze */}
                  <Button
                    onClick={() => openWaze(selected.address)}
                    className="w-full h-14 bg-[#33ccff] hover:bg-[#2bb8e8] text-black font-bold text-lg mb-6 gap-2 rounded-full"
                    data-testid="tech-open-waze"
                  >
                    <NavigationArrow size={24} weight="fill" />
                    Ouvrir dans Waze
                  </Button>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    {selected.status === 'validee' && (
                      <ActionButton
                        onClick={() => updateStatus('en_route')}
                        loading={actionLoading}
                        icon={<Play size={24} weight="fill" />}
                        label="Départ"
                        testId="tech-depart"
                      />
                    )}
                    
                    {selected.status === 'en_route' && (
                      <ActionButton
                        onClick={() => updateStatus('en_cours')}
                        loading={actionLoading}
                        icon={<Flag size={24} weight="fill" />}
                        label="Début d'intervention"
                        testId="tech-arrival"
                      />
                    )}
                    
                    {selected.status === 'en_cours' && (
                      <ActionButton
                        onClick={() => updateStatus('terminee')}
                        loading={actionLoading}
                        icon={<CheckCircle size={24} weight="fill" />}
                        label="Fin d'intervention"
                        testId="tech-complete"
                      />
                    )}
                    
                    {selected.status === 'terminee' && (
                      <ActionButton
                        onClick={() => updateStatus('encaissee')}
                        loading={actionLoading}
                        icon={<CurrencyEur size={24} weight="fill" />}
                        label="Encaissement validé"
                        testId="tech-cashed"
                        variant="success"
                      />
                    )}
                    
                    {selected.status === 'encaissee' && (
                      <div className="text-center py-4 text-green-600">
                        <CheckCircle size={48} className="mx-auto mb-2" />
                        <p className="font-semibold">Intervention terminée</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function InterventionListCard({ intervention, onClick, isLate, isFuture = false }) {
  const isCompleted = ['terminee', 'encaissee'].includes(intervention.status);
  
  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${
        isCompleted 
          ? 'border-gray-200 opacity-70' 
          : isFuture 
            ? 'border-blue-200 hover:border-blue-400 hover:shadow-md'
            : 'border-gray-200 hover:border-reco-400 hover:shadow-md'
      }`}
      data-testid={`tech-card-${intervention.id}`}
    >
      {isLate && (
        <div className="flex items-center gap-2 text-red-500 text-sm mb-3 animate-pulse">
          <Warning size={16} weight="fill" />
          <span>Attention — Vous êtes en retard</span>
        </div>
      )}
      
      <div className="flex items-start justify-between mb-2">
        <h3 className={`font-semibold ${isCompleted ? 'text-gray-500' : 'text-gray-900'}`}>
          {intervention.client_name}
        </h3>
        <StatusBadge status={intervention.status} />
      </div>
      
      <div className="flex items-center gap-2 text-gray-500 text-sm mb-2">
        <MapPin size={14} />
        <span className="line-clamp-1">{intervention.address}</span>
      </div>
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-gray-600 text-sm">
          <CalendarBlank size={14} className={isFuture ? 'text-blue-500' : 'text-reco-500'} />
          <span className="font-medium">
            {formatFullDateTime(intervention.announced_time)}
          </span>
        </div>
        {!isCompleted && (
          <CountdownTimer
            announcedTime={intervention.announced_time}
            validatedAt={intervention.validated_at}
            status={intervention.status}
          />
        )}
      </div>
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function ActionButton({ onClick, loading, icon, label, testId, variant = 'primary' }) {
  const baseClasses = "w-full h-16 text-xl font-bold gap-3 transition-all rounded-full";
  const variantClasses = variant === 'success' 
    ? "bg-green-500 hover:bg-green-600 text-white"
    : "bg-reco-500 hover:bg-reco-600 text-white";

  return (
    <Button
      onClick={onClick}
      disabled={loading}
      className={`${baseClasses} ${variantClasses}`}
      data-testid={testId}
    >
      {icon}
      {loading ? 'Chargement...' : label}
    </Button>
  );
}

function LoadingCards() {
  return (
    <div className="space-y-3">
      {[1, 2].map(i => (
        <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/2 mb-3" />
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-4 bg-gray-200 rounded w-1/3" />
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div className="text-center py-8 bg-white border border-gray-200 rounded-xl">
      <Truck size={40} className="mx-auto text-gray-300 mb-3" />
      <p className="text-gray-500 text-sm">{message}</p>
    </div>
  );
}
